import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import "../../design/components/select.css";
import "../../design/components/text-field.css";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { archivedPhoneOf } from "../archive/archive-queries";
import "../archive/archive.css";
import { composePhone, PHONE_PREFIXES, splitPhone } from "../lead-display";
import { isCardLevelError, isVersionConflict, SupersededWriteError, type LeadEdit } from "../lead-queries";
import { CityPicker, type CityValue } from "./CityPicker";

// La modifica sul posto di un dato della scheda (piano M2 Task 18, mock-up `initLeadCard`): la riga «etichetta · valore»
// e, al clic sul valore, la casella del suo tipo. Invio salva, Esc annulla, uscire dal campo salva; senza differenze non
// parte nulla. Il salvataggio è ottimistico: la casella si chiude subito e il valore nuovo si vede mentre il server
// risponde, senza fermare chi scrive; un rifiuto del server sul campo riapre la casella con quello che si era scritto e
// il motivo. Il telefono ha il prefisso separato; numeri ed euro accettano la virgola; la città si sceglie dal selettore.
// Il numero di un Lead archiviato (piano M3 Task 12, D9) lo dice e, per chi ha `trash_restore`, porta agli «Archiviati»
// con quel numero già cercato.

export type EditorValue = string | CityValue;

export type EditorSpec =
  | { kind: "text"; maxLength: number }
  | { kind: "email" }
  | { kind: "phone" }
  | { kind: "number" }
  | { kind: "currency" }
  | { kind: "choice"; options: ReadonlyArray<{ id: string; label: string }> }
  | { kind: "city" };

/** Ciò che diventa un valore scritto: niente da salvare, un errore da mostrare, oppure la modifica da mandare. */
export type Commit = { kind: "same" } | { kind: "invalid"; message: string } | { kind: "save"; edit: LeadEdit };

/**
 * Prefisso e numero della casella del telefono da ciò che contiene: un numero salvato (`+390363000142`) oppure quello
 * scritto prima di un rifiuto del server, nella forma di `composePhone` (`+39 300 000 0999`).
 */
function phoneParts(draft: string): { prefix: string; national: string } {
  const spaced = /^(\+\d{1,3}) (.*)$/.exec(draft);
  const known = spaced ? PHONE_PREFIXES.find((option) => option.value === spaced[1]) : undefined;
  if (spaced && known) return { prefix: known.value, national: spaced[2] ?? "" };
  return splitPhone(draft || null);
}

type EditableFieldProps = {
  /** La base degli `id` della riga e della sua casella. */
  id: string;
  label: string;
  /** Il valore come si legge; `null` quando è vuoto. */
  display: string | null;
  /** Una riga sotto il valore (per esempio «non più attivo»). */
  sub?: ReactNode;
  editable: boolean;
  spec: EditorSpec;
  /** Il contenuto di partenza della casella. */
  initial: EditorValue;
  commit: (value: EditorValue) => Commit;
  onSave: (edit: LeadEdit) => Promise<unknown>;
  /** Un rifiuto della scheda intera (conflitto di versione, permessi): lo dice la pagina. */
  onCardError: (error: unknown) => void;
  tab: TabContext;
  /** La casella si apre appena la riga compare (il nome del secondo titolare dopo «Aggiungi un altro titolare»). */
  startEditing?: boolean;
};

/**
 * La casella aperta: il contenuto, il motivo da mostrare e il focus. Una casella che si riapre dopo la risposta del
 * server prende il focus soltanto se la persona non sta già scrivendo in un'altra (`"ifFree"`), e uscendone non salva
 * (`holdOnBlur`): quello che contiene non è stato salvato, e riparte soltanto con Invio (o si lascia con Esc).
 * `archivedPhone` è il numero di un Lead archiviato che il server ha trovato, da cercare fra gli «Archiviati».
 */
type Editing = { draft: EditorValue; error?: string; archivedPhone?: string | null; focus: FocusRequest; holdOnBlur?: boolean };
type FocusRequest = boolean | "ifFree";

/** Se il focus sta già in un'altra casella: un rifiuto che arriva tardi non lo porta via a chi sta scrivendo. */
function typingElsewhere(): boolean {
  const active = document.activeElement;
  return active instanceof HTMLInputElement || active instanceof HTMLSelectElement || active instanceof HTMLTextAreaElement;
}

export function EditableField({ id, label, display, sub, editable, spec, initial, commit, onSave, onCardError, tab, startEditing }: EditableFieldProps) {
  const t = useT();
  const toast = useToast();
  const canOpenArchived = useAccess().can("trash_restore");
  const [editing, setEditing] = useState<Editing | null>(startEditing && editable ? { draft: initial, focus: true } : null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const focusBack = useRef(false);

  // Dopo Invio o Esc il focus torna sul valore, come nel mock-up; uscendo dal campo resta dove la persona è andata.
  useEffect(() => {
    if (editing || !focusBack.current) return;
    focusBack.current = false;
    buttonRef.current?.focus();
  }, [editing]);

  function close(returnFocus: boolean) {
    focusBack.current = returnFocus;
    setEditing(null);
  }

  /** Il valore scritto: `true` se la casella si chiude. */
  function submit(value: EditorValue, returnFocus: boolean): boolean {
    const result = commit(value);
    if (result.kind === "invalid") {
      setEditing({ draft: value, error: result.message, focus: true });
      return false;
    }
    close(returnFocus);
    if (result.kind === "same") return true;
    onSave(result.edit).then(
      () => toast.show(t("leadCard.data.saved", { field: label }), "success"),
      (error: unknown) => {
        // Se la persona ha già riaperto la casella, quello che sta scrivendo adesso resta com'è.
        const reopen = (message: string, archivedPhone: string | null = null) =>
          setEditing((current) => current ?? { draft: value, error: message, archivedPhone, focus: "ifFree", holdOnBlur: true });
        if (isVersionConflict(error)) {
          // Qualcun altro ha cambiato il Lead (anche un altro dato): la scheda è già riletta, e la modifica scritta torna
          // nella sua casella, con il motivo. Non riparte da sola: Invio la rimanda con la versione nuova.
          onCardError(error);
          reopen(t("leadCard.errors.conflict"));
          return;
        }
        // Fermata da un altro rifiuto della scheda intera (permessi, modello, Lead tolto): lo dice la pagina.
        if (error instanceof SupersededWriteError) return;
        if (isCardLevelError(error)) {
          onCardError(error);
          return;
        }
        // Il numero è di un Lead archiviato: lo si dice, e chi può ripristinarlo va agli «Archiviati» con quel numero.
        const archivedPhone = canOpenArchived ? archivedPhoneOf(error) : null;
        reopen(archivedPhone ? t("leadArchive.phoneOfArchived") : fieldErrorMessage(t, error, spec), archivedPhone);
      },
    );
    return true;
  }

  const shown = display ?? (editable ? t("leadCard.data.empty") : "—");
  return (
    <div className="g-lead-data__row">
      <dt id={`${id}-label`}>{label}</dt>
      <dd>
        {editing ? (
          <FieldEditor
            id={id}
            label={label}
            spec={spec}
            draft={editing.draft}
            error={editing.error}
            archivedPhone={editing.archivedPhone ?? null}
            focus={editing.focus}
            holdOnBlur={editing.holdOnBlur ?? false}
            tab={tab}
            onSubmit={submit}
            onCancel={() => close(true)}
          />
        ) : editable ? (
          <button
            ref={buttonRef}
            type="button"
            className="g-lead-data__value"
            data-empty={display === null ? "true" : undefined}
            data-kind={spec.kind}
            aria-label={t("leadCard.data.editLabel", { field: label, value: shown })}
            onClick={() => setEditing({ draft: initial, focus: true })}
          >
            {shown}
          </button>
        ) : (
          <span className="g-lead-data__text" data-empty={display === null ? "true" : undefined}>{shown}</span>
        )}
        {sub}
      </dd>
    </div>
  );
}

type FieldEditorProps = {
  id: string;
  label: string;
  spec: EditorSpec;
  draft: EditorValue;
  error?: string;
  /** Il numero di un Lead archiviato: sotto il motivo, «Apri gli Archiviati» con quel numero cercato. */
  archivedPhone?: string | null;
  focus: FocusRequest;
  /** Uscendo non salva: la casella resta aperta finché non arrivano Invio o Esc. */
  holdOnBlur: boolean;
  tab: TabContext;
  /** `true` se la casella si chiude (un valore che non va la lascia aperta, con il motivo). */
  onSubmit: (value: EditorValue, returnFocus: boolean) => boolean;
  onCancel: () => void;
};

/** La casella di un tipo: testo, email, telefono con il prefisso, numero, euro, scelta, città. */
export function FieldEditor({ id, label, spec, draft, error, archivedPhone = null, focus, holdOnBlur, tab, onSubmit, onCancel }: FieldEditorProps) {
  const t = useT();
  const [text, setText] = useState(typeof draft === "string" ? draft : "");
  const [city, setCity] = useState<CityValue>(typeof draft === "string" ? { text: draft, match: null } : draft);
  const initialPhone = phoneParts(spec.kind === "phone" && typeof draft === "string" ? draft : "");
  const [prefix, setPrefix] = useState(initialPhone.prefix);
  const [national, setNational] = useState(initialPhone.national);
  // Un testo dell'azienda già su più righe (per esempio importato da Close) si corregge su più righe: una casella di una
  // riga toglierebbe gli a capo appena si scrive. Si decide all'apertura, con quello che la casella contiene.
  const [multiline] = useState(() => spec.kind === "text" && typeof draft === "string" && /[\r\n]/.test(draft));
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  // Chiusa da Invio, Esc o dall'uscita: un'altra uscita (anche quella che il browser manda quando la casella sparisce)
  // non salva una seconda volta.
  const done = useRef(false);

  useEffect(() => {
    if (!focus || (focus === "ifFree" && typingElsewhere())) return;
    const input = inputRef.current ?? textAreaRef.current;
    const target = input ?? selectRef.current;
    target?.focus();
    if (!input) return;
    // La città si riscrive da capo; negli altri campi il cursore va in fondo.
    if (spec.kind === "city") input.select();
    else input.setSelectionRange(input.value.length, input.value.length);
    // Una volta sola, quando la casella si apre (anche quando si riapre dopo un rifiuto del server).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function current(): EditorValue {
    if (spec.kind === "city") return city;
    if (spec.kind === "phone") return composePhone(prefix, national);
    return text;
  }

  function finish(action: "submit" | "cancel", value: EditorValue = current(), returnFocus = true) {
    if (done.current) return;
    if (action === "cancel") {
      done.current = true;
      onCancel();
      return;
    }
    done.current = onSubmit(value, returnFocus);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    // Nella casella su più righe Maiusc+Invio va a capo; Invio da solo salva, come altrove.
    if (event.key === "Enter" && event.shiftKey && event.currentTarget instanceof HTMLTextAreaElement) return;
    if (event.key === "Enter") {
      event.preventDefault();
      finish("submit");
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      finish("cancel");
    }
  }

  function handleBlur(event: FocusEvent<HTMLElement>) {
    // Dal prefisso al numero (o a un comune dell'elenco) si resta nella casella.
    if (event.relatedTarget instanceof Node && containerRef.current?.contains(event.relatedTarget)) return;
    // È l'intera finestra del browser a perdere il focus (Alt-Tab, un'altra applicazione): la casella resta aperta com'è,
    // con quello che si stava scrivendo, e al ritorno il browser vi riporta il cursore. Non è uscire dal campo: il
    // documento non ha più il focus e la casella resta l'elemento attivo della pagina.
    if (!document.hasFocus() && document.activeElement === event.target) return;
    // Riaperta dal server con una modifica non salvata: resta aperta, non la manda né la butta.
    if (holdOnBlur) return;
    // Una città scritta ma non scelta dall'elenco non si salva uscendo: resta quella di prima.
    if (spec.kind === "city" && city.match === null) {
      finish("cancel");
      return;
    }
    // Uscire vale come Invio, con gli stessi rifiuti: un valore che non va (un'email senza dominio, un numero con le
    // lettere) lascia la casella aperta con il motivo e non parte, e il focus resta dove la persona è andata.
    finish("submit", current(), false);
  }

  // `-keys`, non `-hint`: nella riga della città `${id}-hint` è già la riga di provincia e regione del selettore.
  const hintId = `${id}-keys`;
  const errorId = `${id}-error`;
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(" ");
  const invalid = error ? "true" : undefined;
  const errorText = error ? (
    <p id={errorId} className="g-field__error" role="alert">
      <span>{error}</span>
      {archivedPhone ? (
        <Link to="/lead" search={{ archiviati: 1, cercaArchiviati: archivedPhone }} className="g-archived-phone-link">
          {t("leadArchive.openArchived")}
        </Link>
      ) : null}
    </p>
  ) : null;
  const hint = (
    <span id={hintId} className="g-lead-data__hint">
      {t(multiline ? "leadCard.data.editHintMultiline" : "leadCard.data.editHint")}
    </span>
  );

  if (spec.kind === "city") {
    return (
      <div ref={containerRef} className="g-lead-data__editor">
        <CityPicker
          id={id}
          ref={inputRef}
          tab={tab}
          value={city}
          hideLabel
          error={error}
          onChange={(next) => {
            setCity(next);
            // Il clic su un comune, o Invio sul comune evidenziato, salva subito.
            if (next.match) finish("submit", next);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          describedBy={hintId}
        />
        {hint}
      </div>
    );
  }

  if (spec.kind === "phone") {
    return (
      <div ref={containerRef} className="g-lead-data__editor">
        <div className="g-lead-phone">
          <label className="g-visually-hidden" htmlFor={`${id}-prefix`}>{t("leadCard.data.prefix", { field: label })}</label>
          <select
            id={`${id}-prefix`}
            className="g-select"
            value={prefix}
            onChange={(event) => setPrefix(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
          >
            {PHONE_PREFIXES.map((option) => (
              <option key={option.value} value={option.value}>{t(option.label)}</option>
            ))}
          </select>
          <label className="g-visually-hidden" htmlFor={id}>{label}</label>
          <input
            ref={inputRef}
            id={id}
            className="g-field__input"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            maxLength={40}
            value={national}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            onChange={(event) => setNational(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
          />
        </div>
        {hint}
        {errorText}
      </div>
    );
  }

  if (spec.kind === "choice") {
    return (
      <div ref={containerRef} className="g-lead-data__editor">
        <label className="g-visually-hidden" htmlFor={id}>{label}</label>
        <select
          ref={selectRef}
          id={id}
          className="g-select"
          value={text}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        >
          <option value="">{t("leadCard.data.choiceNone")}</option>
          {spec.options.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        {hint}
        {errorText}
      </div>
    );
  }

  if (multiline && spec.kind === "text") {
    return (
      <div ref={containerRef} className="g-lead-data__editor">
        <label className="g-visually-hidden" htmlFor={id}>{label}</label>
        <textarea
          ref={textAreaRef}
          id={id}
          className="g-field__input g-field__input--multiline"
          rows={Math.min(Math.max(text.split("\n").length, 2), 6)}
          maxLength={spec.maxLength}
          value={text}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {hint}
        {errorText}
      </div>
    );
  }

  const numeric = spec.kind === "number" || spec.kind === "currency";
  return (
    <div ref={containerRef} className="g-lead-data__editor">
      <label className="g-visually-hidden" htmlFor={id}>{label}</label>
      <span className="g-lead-data__input" data-suffix={spec.kind === "currency" ? "true" : undefined}>
        <input
          ref={inputRef}
          id={id}
          className="g-field__input"
          type="text"
          inputMode={numeric ? "decimal" : spec.kind === "email" ? "email" : undefined}
          autoComplete="off"
          spellCheck={spec.kind === "email" ? false : undefined}
          maxLength={spec.kind === "text" ? spec.maxLength : spec.kind === "email" ? 254 : 40}
          value={text}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {spec.kind === "currency" ? (
          <span className="g-lead-data__suffix" aria-hidden="true">
            {t("leadCard.data.euro")}
          </span>
        ) : null}
      </span>
      {hint}
      {errorText}
    </div>
  );
}

/** Il rifiuto del server spiegato sul campo che lo riguarda. */
export function fieldErrorMessage(t: Translate, error: unknown, spec: EditorSpec): string {
  if (!(error instanceof ApiRequestError)) return messageForError(t, error);
  const detail = (key: string) => {
    const value = error.details?.[key];
    return typeof value === "string" ? value : null;
  };
  switch (error.code) {
    case "invalid_phone": {
      const suggestion = detail("suggestion");
      if (suggestion === "landline") return detail("holder") === "second" ? t("leadCard.errors.secondHolderLandline") : t("leadCard.errors.looksLandline");
      if (suggestion === "mobile") return t("leadCard.errors.looksMobile");
      return t("leadCard.errors.phone");
    }
    case "invalid_field_value":
      if (spec.kind === "city") return t("leadCard.errors.city");
      if (spec.kind === "number" || spec.kind === "currency") return t("leadCard.errors.number");
      return messageForError(t, error);
    case "validation_failed":
      if (spec.kind === "email") return t("leadCard.errors.email");
      if (spec.kind === "phone") return t("leadCard.errors.phone");
      if (spec.kind === "number" || spec.kind === "currency") return t("leadCard.errors.number");
      return messageForError(t, error);
    default:
      return messageForError(t, error);
  }
}
